import { Check, Copy } from "lucide-react";
import { useClipboardCopy } from "../hooks/useClipboardCopy.js";
import { Tooltip } from "../molecules/Tooltip/index.js";

/**
 * PB-5276 — "Copy error details", shared by every Player AI failure surface.
 *
 * Data-blind: `details` is opaque text the host already composed, and this
 * never parses, inspects or filters it. Keeping the rule about which fields are
 * safe to copy in ONE place — with the host that owns the vocabulary — is the
 * point; a second implementation here would be a second place to get it wrong.
 */
export function CopyErrorDetailsButton({
  details,
  className,
}: {
  readonly details: string;
  readonly className?: string;
}) {
  /* The clipboard write, its failure report and the 2-second reset live in
     the shared hook (codeblock), so this control and CodeBlock cannot drift.
     Everything below — markup, labels, announcements — is unchanged. */
  const { state: copyState, copy } = useClipboardCopy();

  const label =
    copyState === "copied"
      ? "Error details copied"
      : copyState === "failed"
        ? "Couldn’t copy error details"
        : "Copy error details";

  return (
    <>
      {/* The hint is the resting name, so it does not describe the button a
          second time. */}
      <Tooltip content="Copy error details" describeChild={false}>
        <button
          type="button"
          onClick={() => void copy(details)}
          aria-label={label}
          className={
            className ??
            "inline-flex items-center gap-1.5 rounded-full border border-pb-border bg-pb-card px-3 py-1.5 text-xs font-medium text-pb-text-muted transition-colors hover:text-pb-text"
          }
        >
          {copyState === "copied" ? (
            <Check aria-hidden="true" className="h-3.5 w-3.5" />
          ) : (
            <Copy aria-hidden="true" className="h-3.5 w-3.5" />
          )}
          <span aria-hidden="true">
            {copyState === "copied"
              ? "Copied"
              : copyState === "failed"
                ? "Couldn’t copy"
                : "Copy details"}
          </span>
        </button>
      </Tooltip>
      {/*
        The label above changes with the state, but a label change on a control
        that already holds focus is not reliably announced. This region is, and
        it stays empty until there is something to say.
        DS keep (PR 3): a screen-reader-only live region, not a status
        indicator.
      */}
      <span role="status" className="sr-only">
        {copyState === "copied"
          ? "Error details copied to the clipboard."
          : copyState === "failed"
            ? "Couldn’t copy the error details."
            : ""}
      </span>
    </>
  );
}
